import { ArrowLeft, Check, ChevronDown, ImageIcon, Minus, Plus, Save, Search, Trash2, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    getAttributes,
    getServiceDetail,
    getServices,
    getServiceVariants,
    updateCombo,
    updateService,
    updateVariant,
    updateVariantAttributes,
    uploadImage,
} from '../../../api/service.api';
import styles from '../../../assets/scss/pages/ServiceEdit.module.scss';
import createStyles from '../../../assets/scss/pages/ServiceCreate.module.scss';
import KeypadInput from '../../../components/common/KeypadInput';
import { formatCurrency } from '../../../utils/formatters';
import { useDebounce } from '../../../hooks/useDebounce';
import { useToastContext } from '../../../contexts/ToastContext';

const UNIT_SUGGESTIONS = ['Cái', 'Lượt', 'Chai', 'Kg', 'Tạ', 'Tấn', 'Hộp', 'Bộ', 'Lọ', 'Tuýp'];

function normalize(str) {
    return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, '')
        .toLowerCase();
}

// Ô nhập tiền: bấm vào bung bàn phím số ảo (dùng chuột). Giữ contract cũ —
// value là số, onChange trả về số.
function MoneyInput({ value, onChange, placeholder, hasError, title }) {
    return (
        <KeypadInput
            className={`${styles.formInput}${hasError ? ` ${styles.inputError}` : ''}`}
            value={value > 0 ? value : ''}
            onChange={(raw) => onChange(Number(raw) || 0)}
            placeholder={placeholder || '0'}
            title={title || 'Nhập tiền'}
            mode="money"
            suffix="đ"
        />
    );
}

function getChangedFields(initial, current) {
    const changed = {};
    for (const key of Object.keys(current)) {
        if (JSON.stringify(initial[key]) !== JSON.stringify(current[key])) {
            changed[key] = current[key];
        }
    }
    return changed;
}

export default function ServiceEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    // Service state
    const [serviceForm, setServiceForm] = useState({
        name: '',
        description: '',
        displayLocation: '',
        internalNote: '',
        isWeb: false,
        price: 0,
    });
    const [initialServiceForm, setInitialServiceForm] = useState(null);
    const [serviceType, setServiceType] = useState('');
    const [serviceImg, setServiceImg] = useState('');

    // Variants state
    const [variants, setVariants] = useState([]);
    const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
    const [variantForm, setVariantForm] = useState({
        sku: '',
        barcode: '',
        unit: '',
        importPrice: 0,
        retailPrice: 0,
        salePrice: 0,
        quantity: 0,
        notSell: 0,
        alert: 0,
        inStock: true,
        durationMinutes: null,
    });
    const [initialVariantForm, setInitialVariantForm] = useState(null);

    // Attributes
    const [allAttributes, setAllAttributes] = useState([]);
    const [selectedAttrValues, setSelectedAttrValues] = useState({}); // { attrId: valueId }
    const [initialAttrValues, setInitialAttrValues] = useState({});

    // Combo state
    const [comboItems, setComboItems] = useState([]);
    const [initialComboItems, setInitialComboItems] = useState([]);
    const [showComboCatalog, setShowComboCatalog] = useState(false);
    const [allCatalogServices, setAllCatalogServices] = useState([]);
    const [catalogTab, setCatalogTab] = useState('all');
    const [catalogSearch, setCatalogSearch] = useState('');
    const [variantPicker, setVariantPicker] = useState(null); // { service, variants }
    const [nameEdited, setNameEdited] = useState(false);
    const debouncedCatalogSearch = useDebounce(catalogSearch, 300);

    // UI
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState({});
    const [toastMsg, setToastMsg] = useState(null);
    const [showUnitSuggestions, setShowUnitSuggestions] = useState(false);
    const [attrSearch, setAttrSearch] = useState('');

    const showToast = useCallback((type, text, duration = 1500) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    const { showError } = useToastContext();

    // === Load variant form from variant object ===
    const loadVariantForm = useCallback((variant) => {
        if (!variant) return;
        const vf = {
            sku: variant.sku || '',
            barcode: variant.barcode || '',
            unit: variant.unit || '',
            importPrice: Number(variant.importPrice) || 0,
            retailPrice: Number(variant.retailPrice) || 0,
            salePrice: Number(variant.salePrice) || 0,
            quantity: Number(variant.quantity) || 0,
            notSell: Number(variant.notSell) || 0,
            alert: Number(variant.alert) || 0,
            inStock: variant.inStock !== false,
            durationMinutes: variant.durationMinutes || null,
        };
        setVariantForm(vf);
        setInitialVariantForm(vf);

        // Build selected attribute values map
        const attrMap = {};
        (variant.attributeValues || []).forEach((av) => {
            if (av.attribute?.id) {
                attrMap[av.attribute.id] = av.id;
            }
        });
        setSelectedAttrValues(attrMap);
        setInitialAttrValues(attrMap);
    }, []);

    // === Fetch all data ===
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [serviceRes, attrsRes] = await Promise.all([
                getServiceVariants(id),
                getAttributes(),
            ]);
            const service = serviceRes.data.variant || serviceRes.data;

            const sf = {
                name: service.name || '',
                description: service.description || '',
                displayLocation: service.displayLocation || '',
                internalNote: service.internalNote || '',
                isWeb: service.isWeb || false,
                price: Math.round(parseFloat(service.price || 0)),
            };
            setServiceForm(sf);
            setInitialServiceForm(sf);
            setServiceType(service.typeService || '');
            setServiceImg(service.urlImg || '');

            const vList = service.variants || [];
            setVariants(vList);
            setSelectedVariantIdx(0);
            if (vList.length > 0) loadVariantForm(vList[0]);

            setAllAttributes(attrsRes.data.attributes || []);

            // Fetch combo items nếu là combo
            if (service.typeService === 'combo') {
                try {
                    const { data: detailData } = await getServiceDetail(id);
                    const detail = detailData.service || detailData;
                    const items = (detail.comboItems || []).map((ci) => ({
                        comboItemId: ci.id,
                        variantId: ci.variantId,
                        serviceName: ci.variant?.service?.name || '',
                        variantName: (ci.variant?.attributeValues || []).map((v) => v.value).join(' - '),
                        quantity: ci.quantity || 1,
                        price: (ci.variant?.salePrice > 0 ? ci.variant.salePrice : ci.variant?.retailPrice) || 0,
                    }));
                    setComboItems(items);
                    setInitialComboItems(items);
                    if (sf.name) setNameEdited(true);
                } catch { /* ignore */ }
            }
        } catch (err) {
            console.error('Error fetching:', err);
        } finally {
            setLoading(false);
        }
    }, [id, loadVariantForm]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // === Switch variant tab ===
    const handleVariantTabClick = (idx) => {
        setSelectedVariantIdx(idx);
        loadVariantForm(variants[idx]);
    };

    // === Attribute selection ===
    const handleSelectAttrValue = (attrId, valueId) => {
        setSelectedAttrValues((prev) => {
            if (prev[attrId] === valueId) {
                const next = { ...prev };
                delete next[attrId];
                return next;
            }
            return { ...prev, [attrId]: valueId };
        });
    };

    // === Combo functions ===
    const normalizeComboForCompare = (items) =>
        (items || [])
            .map((it) => ({ variantId: Number(it.variantId) || 0, quantity: Number(it.quantity) || 0 }))
            .sort((a, b) => a.variantId - b.variantId);

    const fetchComboCatalog = useCallback(async () => {
        try {
            const [dvRes, spRes] = await Promise.all([
                getServices({ limit: 1000, typeService: 'dichvu' }),
                getServices({ limit: 1000, typeService: 'sanpham' }),
            ]);
            const dv = (dvRes.data.services || []).filter((s) => !s.isDeleted).map((s) => ({ ...s, _type: 'dichvu' }));
            const sp = (spRes.data.services || []).filter((s) => !s.isDeleted).map((s) => ({ ...s, _type: 'sanpham' }));
            setAllCatalogServices([...dv, ...sp]);
        } catch {
            setAllCatalogServices([]);
        }
    }, []);

    const openComboCatalog = () => {
        setCatalogSearch('');
        setCatalogTab('all');
        setVariantPicker(null);
        fetchComboCatalog();
        setShowComboCatalog(true);
    };

    const closeComboCatalog = () => setShowComboCatalog(false);

    const autoGenerateComboName = (items) => {
        if (items.length === 0) return '';
        const names = items.slice(0, 2).map((it) => it.serviceName);
        return names.join(' + ') + (items.length > 2 ? ` +${items.length - 2}` : '');
    };

    const autoCalcComboPrice = (items) =>
        items.reduce((sum, it) => sum + (Number(it.price) || 0) * (parseInt(it.quantity) || 1), 0);

    const syncComboMeta = (newItems) => {
        if (!nameEdited) {
            setServiceForm((p) => ({ ...p, name: autoGenerateComboName(newItems) }));
        }
        setServiceForm((p) => ({ ...p, price: Math.round(autoCalcComboPrice(newItems)) }));
    };

    const getVariantLabel = (v) => {
        if (v?.attributeValues?.length > 0) return v.attributeValues.map((a) => a.value).join(' - ');
        return v?.sku || 'Mặc định';
    };

    const getItemPrice = (variant) =>
        (variant?.salePrice && Number(variant.salePrice) > 0 ? Number(variant.salePrice) : Number(variant?.retailPrice)) || 0;

    const addItemWithVariant = (service, variant) => {
        const variantId = variant.id;
        const price = getItemPrice(variant);
        const serviceName = service.name || '';
        const variantName = getVariantLabel(variant);

        setComboItems((prev) => {
            const idx = prev.findIndex((it) => it.variantId === variantId);
            let newItems;
            if (idx >= 0) {
                newItems = prev.map((it, i) => i === idx ? { ...it, quantity: (parseInt(it.quantity) || 1) + 1 } : it);
            } else {
                newItems = [...prev, { variantId, serviceName, variantName, quantity: 1, price }];
            }
            setTimeout(() => syncComboMeta(newItems), 0);
            return newItems;
        });
        setVariantPicker(null);
    };

    const addCatalogItem = (service) => {
        const variants = service.variants || [];
        if (variants.length === 0) return;
        if (variants.length === 1) {
            addItemWithVariant(service, variants[0]);
        } else {
            setVariantPicker((prev) => prev?.service?.id === service.id ? null : { service, variants });
        }
    };

    const removeComboItem = (idx) => {
        setComboItems((prev) => {
            const newItems = prev.filter((_, i) => i !== idx);
            setTimeout(() => syncComboMeta(newItems), 0);
            return newItems;
        });
    };

    const updateComboItemQty = (idx, delta) => {
        setComboItems((prev) => {
            const newItems = prev.map((it, i) =>
                i === idx ? { ...it, quantity: Math.max(1, (parseInt(it.quantity) || 1) + delta) } : it,
            );
            setTimeout(() => syncComboMeta(newItems), 0);
            return newItems;
        });
    };

    const isInCombo = (serviceId) => comboItems.some((it) => {
        // Check if any combo item belongs to this service
        const svc = allCatalogServices.find((s) => s.id === serviceId);
        if (!svc) return false;
        return (svc.variants || []).some((v) => v.id === it.variantId);
    });

    const filteredCatalogServices = allCatalogServices.filter((s) => {
        if (catalogTab !== 'all' && s._type !== catalogTab) return false;
        if (!debouncedCatalogSearch.trim()) return true;
        const kw = debouncedCatalogSearch.trim().toLowerCase().replace(/\s/g, '');
        const kwNoDiac = kw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const name = (s.name || '').toLowerCase().replace(/\s/g, '');
        const nameNoDiac = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        return nameNoDiac.includes(kwNoDiac) || name.includes(kw);
    });

    // === Image upload (auto-save like app) ===
    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const { data } = await uploadImage(file);
            const newUrl = data.url;
            await updateService(id, { urlImg: newUrl });
            setServiceImg(newUrl);
            showToast('success', 'Cập nhật ảnh thành công');
        } catch {
            showToast('error', 'Không thể upload ảnh', 2500);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // === Validate ===
    const validate = () => {
        const errs = {};
        if (!serviceForm.name.trim()) errs.name = 'Tên không được để trống';
        if (serviceType === 'combo' && comboItems.length === 0) {
            errs.submit = 'Combo phải có ít nhất 1 dịch vụ hoặc sản phẩm';
        }
        if (serviceType !== 'combo' && variants.length > 0 && variantForm.retailPrice <= 0) {
            errs.retailPrice = 'Phải lớn hơn 0';
        }
        if (serviceType === 'sanpham' && variantForm.notSell > variantForm.quantity) {
            errs.notSell = 'Không được lớn hơn tồn kho';
        }
        return errs;
    };

    // === Save (change detection like app) ===
    const handleSave = async () => {
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            showError(Object.values(errs)[0] || 'Vui lòng kiểm tra lại thông tin');
            return;
        }

        setSaving(true);
        setErrors({});
        let hasUpdate = false;

        try {
            const serviceChanges = getChangedFields(initialServiceForm, serviceForm);
            const hasComboChanged = isCombo &&
                JSON.stringify(normalizeComboForCompare(comboItems)) !==
                JSON.stringify(normalizeComboForCompare(initialComboItems));

            if (isCombo) {
                // Combo: dùng PUT /services/combo/{id}
                if (Object.keys(serviceChanges).length > 0 || hasComboChanged) {
                    const comboPayload = {
                        ...initialServiceForm,
                        ...serviceChanges,
                        urlImg: serviceImg,
                        variants: comboItems.map((it) => ({
                            variantId: it.variantId,
                            quantity: parseInt(it.quantity) || 1,
                        })),
                        typeService: 'combo',
                    };
                    await updateCombo(id, comboPayload);
                    hasUpdate = true;
                }
            } else {
                // Dịch vụ / Sản phẩm
                // 1) Service field changes
                if (Object.keys(serviceChanges).length > 0) {
                    await updateService(id, serviceChanges);
                    hasUpdate = true;
                }

                // 2) Variant field changes
                const sv = variants[selectedVariantIdx];
                if (sv) {
                    const variantChanges = getChangedFields(initialVariantForm, variantForm);
                    if (variantChanges.durationMinutes !== undefined) {
                        variantChanges.durationMinutes = variantChanges.durationMinutes
                            ? parseInt(variantChanges.durationMinutes, 10)
                            : null;
                    }
                    if (Object.keys(variantChanges).length > 0) {
                        await updateVariant(sv.id, variantChanges);
                        hasUpdate = true;
                    }

                    // 3) Attribute changes
                    const attrChanged =
                        JSON.stringify(initialAttrValues) !== JSON.stringify(selectedAttrValues);
                    if (attrChanged) {
                        await updateVariantAttributes(sv.id, Object.values(selectedAttrValues));
                        hasUpdate = true;
                    }
                }
            }

            if (!hasUpdate) {
                showToast('error', 'Không có thay đổi để cập nhật', 2000);
                setSaving(false);
                return;
            }

            showToast('success', 'Cập nhật thành công!');
            setTimeout(() => navigate(`/admin/services/${id}`), 1200);
        } catch (err) {
            const msg = err.response?.data?.message || 'Cập nhật thất bại';
            setErrors({ submit: msg });
            showError(msg);
        } finally {
            setSaving(false);
        }
    };

    const selectedVariant = variants[selectedVariantIdx];
    const isProduct = serviceType === 'sanpham';
    const isDichvu = serviceType === 'dichvu';
    const isCombo = serviceType === 'combo';

    // === Loading skeleton ===
    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <div className={styles.skeleton} style={{ width: 40, height: 40, borderRadius: 10 }} />
                        <div>
                            <div className={styles.skeleton} style={{ width: 100, height: 14, borderRadius: 6, marginBottom: 6 }} />
                            <div className={styles.skeleton} style={{ width: 180, height: 26, borderRadius: 8 }} />
                        </div>
                    </div>
                    <div className={styles.skeleton} style={{ width: 140, height: 42, borderRadius: 10 }} />
                </div>
                <div className={styles.layout}>
                    <div className={styles.layoutLeft}>
                        {[1, 2].map((i) => (
                            <div key={i} className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <div className={styles.skeleton} style={{ width: 160, height: 20, borderRadius: 6 }} />
                                </div>
                                <div className={styles.cardBody}>
                                    <div className={styles.formGrid}>
                                        {[1, 2, 3, 4].map((j) => (
                                            <div key={j} className={styles.formGroup}>
                                                <div className={styles.skeleton} style={{ width: 80, height: 14, borderRadius: 6 }} />
                                                <div className={styles.skeleton} style={{ height: 44, borderRadius: 10 }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={styles.layoutRight}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.skeleton} style={{ width: 80, height: 20, borderRadius: 6 }} />
                            </div>
                            <div className={styles.cardBody}>
                                <div className={styles.skeleton} style={{ width: '100%', aspectRatio: '1', borderRadius: 14 }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const filteredUnitSuggestions = UNIT_SUGGESTIONS.filter(
        (u) => !variantForm.unit || u.toLowerCase().includes(variantForm.unit.toLowerCase()),
    );

    function getVariantTabName(v) {
        if (v?.attributeValues?.length > 0) return v.attributeValues.map((a) => a.value).join(' - ');
        return v?.sku || 'Mặc định';
    }

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <div>
                        <div className={styles.headerSub}>{serviceForm.name || '...'}</div>
                        <h1 className={styles.title}>Chỉnh sửa</h1>
                    </div>
                </div>
                <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                    <Save size={18} />
                    {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
                </button>
            </div>

            {errors.submit && <div className={styles.errorMsg}>{errors.submit}</div>}

            <div className={styles.layout}>
                {/* ===== CỘT TRÁI ===== */}
                <div className={styles.layoutLeft}>
                    {/* Card: Thông tin dịch vụ */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>Thông tin chung</div>
                        <div className={styles.cardBody}>
                            <div className={styles.formGrid}>
                                <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                                    <label className={styles.formLabel}>Tên <span>*</span></label>
                                    <input
                                        className={`${styles.formInput}${errors.name ? ` ${styles.inputError}` : ''}`}
                                        value={serviceForm.name}
                                        onChange={(e) => {
                                            setServiceForm((p) => ({ ...p, name: e.target.value }));
                                            setErrors((p) => ({ ...p, name: '' }));
                                            if (isCombo) setNameEdited(true);
                                        }}
                                        placeholder={isCombo ? 'Tự động tạo từ mục đã chọn' : 'Tên dịch vụ / sản phẩm'}
                                    />
                                    {errors.name && <span className={styles.formError}>{errors.name}</span>}
                                </div>
                                <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                                    <label className={styles.formLabel}>Mô tả</label>
                                    <textarea
                                        className={styles.formTextarea}
                                        value={serviceForm.description}
                                        onChange={(e) => setServiceForm((p) => ({ ...p, description: e.target.value }))}
                                        placeholder="Mô tả..."
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Vị trí trưng bày</label>
                                    <input
                                        className={styles.formInput}
                                        value={serviceForm.displayLocation}
                                        onChange={(e) => setServiceForm((p) => ({ ...p, displayLocation: e.target.value }))}
                                        placeholder="Tuỳ chọn"
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Ghi chú nội bộ</label>
                                    <input
                                        className={styles.formInput}
                                        value={serviceForm.internalNote}
                                        onChange={(e) => setServiceForm((p) => ({ ...p, internalNote: e.target.value }))}
                                        placeholder="Tuỳ chọn"
                                    />
                                </div>
                            </div>
                            <label className={styles.toggleRow}>
                                <input
                                    type="checkbox"
                                    checked={serviceForm.isWeb}
                                    onChange={(e) => setServiceForm((p) => ({ ...p, isWeb: e.target.checked }))}
                                />
                                Hiển thị trên website
                            </label>
                        </div>
                    </div>

                    {/* Card: Biến thể (không cho combo) */}
                    {!isCombo && selectedVariant && (
                        <div className={styles.card}>
                            {variants.length > 1 && (
                                <div className={styles.variantTabs}>
                                    {variants.map((v, idx) => (
                                        <button
                                            key={v.id}
                                            className={`${styles.variantTab}${idx === selectedVariantIdx ? ` ${styles.variantTabActive}` : ''}`}
                                            onClick={() => handleVariantTabClick(idx)}
                                        >
                                            {getVariantTabName(v)}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className={styles.cardHeader}>
                                {variants.length > 1
                                    ? `Biến thể: ${getVariantTabName(selectedVariant)}`
                                    : 'Thông tin biến thể'}
                            </div>
                            <div className={styles.cardBody}>
                                <div className={styles.formGridThree}>
                                    {/* Đơn vị */}
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Đơn vị</label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                className={styles.formInput}
                                                value={variantForm.unit}
                                                onChange={(e) => setVariantForm((p) => ({ ...p, unit: e.target.value }))}
                                                onFocus={() => setShowUnitSuggestions(true)}
                                                onBlur={() => setTimeout(() => setShowUnitSuggestions(false), 150)}
                                                placeholder="VD: Cái, Lượt..."
                                            />
                                            {showUnitSuggestions && filteredUnitSuggestions.length > 0 && (
                                                <div style={{
                                                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                                                    background: '#fff', border: '1.5px solid #ECEEF2', borderRadius: 10,
                                                    boxShadow: '0 4px 8px rgba(0,0,0,0.06)', zIndex: 10, overflow: 'hidden',
                                                }}>
                                                    {filteredUnitSuggestions.map((u) => (
                                                        <button key={u} style={{
                                                            display: 'block', width: '100%', padding: '10px 14px',
                                                            border: 'none', background: 'none', fontSize: 14,
                                                            textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                                                        }} onMouseDown={() => {
                                                            setVariantForm((p) => ({ ...p, unit: u }));
                                                            setShowUnitSuggestions(false);
                                                        }}>{u}</button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* SKU */}
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Mã SKU</label>
                                        <input
                                            className={styles.formInput}
                                            value={variantForm.sku}
                                            onChange={(e) => setVariantForm((p) => ({ ...p, sku: e.target.value }))}
                                            placeholder="Tuỳ chọn"
                                        />
                                    </div>
                                    {/* Barcode */}
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Mã vạch</label>
                                        <input
                                            className={styles.formInput}
                                            value={variantForm.barcode}
                                            onChange={(e) => setVariantForm((p) => ({ ...p, barcode: e.target.value }))}
                                            placeholder="Tuỳ chọn"
                                        />
                                    </div>
                                    {/* Giá nhập */}
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Giá nhập</label>
                                        <MoneyInput
                                            value={variantForm.importPrice}
                                            onChange={(v) => setVariantForm((p) => ({ ...p, importPrice: v }))}
                                            title="Giá nhập"
                                        />
                                    </div>
                                    {/* Giá bán */}
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Giá bán <span>*</span></label>
                                        <MoneyInput
                                            value={variantForm.retailPrice}
                                            onChange={(v) => {
                                                setVariantForm((p) => ({ ...p, retailPrice: v }));
                                                setErrors((p) => ({ ...p, retailPrice: '' }));
                                            }}
                                            hasError={!!errors.retailPrice}
                                            title="Giá bán"
                                        />
                                        {errors.retailPrice && <span className={styles.formError}>{errors.retailPrice}</span>}
                                    </div>
                                    {/* Giá KM */}
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Giá khuyến mãi</label>
                                        <MoneyInput
                                            value={variantForm.salePrice}
                                            onChange={(v) => setVariantForm((p) => ({ ...p, salePrice: v }))}
                                            placeholder="0 (không có)"
                                            title="Giá khuyến mãi"
                                        />
                                    </div>
                                    {/* Thời gian phục vụ - dichvu */}
                                    {isDichvu && (
                                        <div className={styles.formGroup}>
                                            <label className={styles.formLabel}>Thời gian (phút)</label>
                                            <KeypadInput
                                                className={styles.formInput}
                                                mode="integer"
                                                value={variantForm.durationMinutes || ''}
                                                onChange={(raw) => setVariantForm((p) => ({
                                                    ...p,
                                                    durationMinutes: raw ? parseInt(raw, 10) : null,
                                                }))}
                                                placeholder="VD: 60"
                                                title="Thời gian (phút)"
                                                suffix="phút"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Tồn kho - sanpham */}
                                {isProduct && (
                                    <>
                                        <div style={{ height: 20 }} />
                                        <div className={styles.formGridThree}>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Tồn kho</label>
                                                <KeypadInput
                                                    className={styles.formInput}
                                                    mode="integer"
                                                    value={variantForm.quantity || ''}
                                                    onChange={(raw) => setVariantForm((p) => ({
                                                        ...p, quantity: parseInt(raw) || 0,
                                                    }))}
                                                    placeholder="0"
                                                    title="Tồn kho"
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Không bán</label>
                                                <KeypadInput
                                                    className={`${styles.formInput}${errors.notSell ? ` ${styles.inputError}` : ''}`}
                                                    mode="integer"
                                                    value={variantForm.notSell || ''}
                                                    onChange={(raw) => {
                                                        setVariantForm((p) => ({ ...p, notSell: parseInt(raw) || 0 }));
                                                        setErrors((p) => ({ ...p, notSell: '' }));
                                                    }}
                                                    placeholder="0"
                                                    title="Không bán"
                                                />
                                                {errors.notSell && <span className={styles.formError}>{errors.notSell}</span>}
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Cảnh báo tồn</label>
                                                <KeypadInput
                                                    className={styles.formInput}
                                                    mode="integer"
                                                    value={variantForm.alert || ''}
                                                    onChange={(raw) => setVariantForm((p) => ({
                                                        ...p, alert: parseInt(raw) || 0,
                                                    }))}
                                                    placeholder="0"
                                                    title="Cảnh báo tồn"
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Card: Thuộc tính biến thể */}
                    {!isCombo && selectedVariant && allAttributes.length > 0 && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span>Thuộc tính biến thể</span>
                                {Object.keys(selectedAttrValues).length > 0 && (
                                    <span className={styles.attrCount}>
                                        {Object.keys(selectedAttrValues).length} đã chọn
                                    </span>
                                )}
                            </div>
                            <div className={styles.cardBody}>
                                <div className={styles.attrSearchWrap}>
                                    <Search size={16} />
                                    <input
                                        className={styles.attrSearchInput}
                                        value={attrSearch}
                                        onChange={(e) => setAttrSearch(e.target.value)}
                                        placeholder="Tìm thuộc tính..."
                                    />
                                    {attrSearch && (
                                        <button className={styles.attrSearchClear} onClick={() => setAttrSearch('')}>
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                                {allAttributes
                                    .filter((attr) => {
                                        if (!attrSearch.trim()) return true;
                                        const q = normalize(attrSearch);
                                        if (normalize(attr.name).includes(q)) return true;
                                        return (attr.attributeValues || []).some((v) =>
                                            normalize(v.value).includes(q),
                                        );
                                    })
                                    .map((attr) => (
                                        <div key={attr.id} className={styles.attrSection}>
                                            <div className={styles.attrTitle}>{attr.name}</div>
                                            <div className={styles.attrChips}>
                                                {(attr.attributeValues || []).map((val) => {
                                                    const isSelected = selectedAttrValues[attr.id] === val.id;
                                                    return (
                                                        <button
                                                            key={val.id}
                                                            className={`${styles.attrChip}${isSelected ? ` ${styles.attrChipActive}` : ''}`}
                                                            onClick={() => handleSelectAttrValue(attr.id, val.id)}
                                                        >
                                                            {isSelected && <Check size={12} />}
                                                            {val.value}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Combo: giá + items */}
                    {isCombo && (
                        <>
                            <div className={styles.card}>
                                <div className={styles.cardHeader}>Giá combo</div>
                                <div className={styles.cardBody}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Giá bán</label>
                                        <MoneyInput
                                            value={serviceForm.price}
                                            onChange={(v) => setServiceForm((p) => ({ ...p, price: v }))}
                                            title="Giá combo"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <span>Trong combo ({comboItems.length})</span>
                                    <button className={styles.comboAddBtn} onClick={showComboCatalog ? closeComboCatalog : openComboCatalog}>
                                        {showComboCatalog ? <><X size={14} /> Đóng</> : <><Plus size={14} /> Thêm</>}
                                    </button>
                                </div>
                                <div className={styles.cardBody}>
                                    {comboItems.length === 0 ? (
                                        <div className={styles.comboEmpty}>Chưa có mục nào trong combo</div>
                                    ) : (
                                        comboItems.map((ci, idx) => (
                                            <div key={idx} className={styles.comboItem}>
                                                <div className={styles.comboItemInfo}>
                                                    <div className={styles.comboItemName}>{ci.serviceName}</div>
                                                    {ci.variantName && (
                                                        <div className={styles.comboItemVariant}>{ci.variantName}</div>
                                                    )}
                                                    <div className={styles.comboItemPrice}>{formatCurrency(ci.price)}</div>
                                                </div>
                                                <div className={styles.comboItemActions}>
                                                    <button className={styles.comboQtyBtn} onClick={() => updateComboItemQty(idx, -1)}>
                                                        <Minus size={14} />
                                                    </button>
                                                    <span className={styles.comboQtyValue}>{ci.quantity}</span>
                                                    <button className={styles.comboQtyBtn} onClick={() => updateComboItemQty(idx, 1)}>
                                                        <Plus size={14} />
                                                    </button>
                                                    <button className={styles.comboRemoveBtn} onClick={() => removeComboItem(idx)}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* ===== CỘT PHẢI ===== */}
                <div className={styles.layoutRight}>
                    {/* Combo catalog sidebar — thay ảnh khi đang thêm */}
                    {isCombo && showComboCatalog ? (
                        <div className={createStyles.comboCatalog}>
                            <div className={createStyles.comboCatalogHeader}>
                                <h3 className={createStyles.comboCatalogTitle}>Chọn dịch vụ / sản phẩm</h3>
                            </div>
                            <div className={createStyles.comboCatalogTabs}>
                                {[
                                    { value: 'all', label: 'Tất cả' },
                                    { value: 'dichvu', label: 'Dịch vụ' },
                                    { value: 'sanpham', label: 'Sản phẩm' },
                                ].map((tab) => (
                                    <button
                                        key={tab.value}
                                        className={`${createStyles.comboCatalogTab}${catalogTab === tab.value ? ` ${createStyles.comboCatalogTabActive}` : ''}`}
                                        onClick={() => setCatalogTab(tab.value)}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                            <div className={createStyles.comboCatalogSearch}>
                                <Search size={16} />
                                <input
                                    value={catalogSearch}
                                    onChange={(e) => setCatalogSearch(e.target.value)}
                                    placeholder="Tìm kiếm..."
                                />
                                {catalogSearch && (
                                    <button onClick={() => setCatalogSearch('')}><X size={14} /></button>
                                )}
                            </div>
                            <div className={createStyles.comboCatalogList}>
                                {filteredCatalogServices.length === 0 ? (
                                    <div className={createStyles.comboCatalogEmpty}>Không tìm thấy</div>
                                ) : (
                                    filteredCatalogServices.map((service) => {
                                        const svcVariants = service.variants || [];
                                        if (svcVariants.length === 0) return null;
                                        const added = isInCombo(service.id);
                                        const isPickerOpen = variantPicker?.service?.id === service.id;
                                        return (
                                            <div key={service.id}>
                                                <div
                                                    className={`${createStyles.comboCatalogItem}${added ? ` ${createStyles.comboCatalogItemAdded}` : ''}`}
                                                    onClick={() => addCatalogItem(service)}
                                                >
                                                    {service.urlImg && (
                                                        <img src={service.urlImg} alt={service.name} className={createStyles.comboCatalogItemImg} />
                                                    )}
                                                    <div className={createStyles.comboCatalogItemInfo}>
                                                        <div className={createStyles.comboCatalogItemName}>{service.name}</div>
                                                        <div className={createStyles.comboCatalogItemPrice}>
                                                            {formatCurrency(getItemPrice(svcVariants[0]))}
                                                            {svcVariants.length > 1 && (
                                                                <span className={createStyles.comboCatalogVariantCount}>
                                                                    {svcVariants.length} loại
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button className={createStyles.comboCatalogItemAdd}>
                                                        {added ? '✓' : svcVariants.length > 1 ? <ChevronDown size={16} /> : <Plus size={16} />}
                                                    </button>
                                                </div>
                                                {isPickerOpen && (
                                                    <div className={createStyles.variantPickerList}>
                                                        <div className={createStyles.variantPickerTitle}>Chọn phân loại:</div>
                                                        {svcVariants.map((v) => {
                                                            const vAdded = comboItems.some((it) => it.variantId === v.id);
                                                            return (
                                                                <div
                                                                    key={v.id}
                                                                    className={`${createStyles.variantPickerItem}${vAdded ? ` ${createStyles.variantPickerItemAdded}` : ''}`}
                                                                    onClick={(e) => { e.stopPropagation(); addItemWithVariant(service, v); }}
                                                                >
                                                                    <span className={createStyles.variantPickerLabel}>{getVariantLabel(v)}</span>
                                                                    <span className={createStyles.variantPickerPrice}>{formatCurrency(getItemPrice(v))}</span>
                                                                    <span className={createStyles.variantPickerAdd}>{vAdded ? '✓' : <Plus size={14} />}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Ảnh */}
                            <div className={styles.card}>
                                <div className={styles.cardHeader}>Ảnh</div>
                                <div className={styles.cardBody}>
                                    <div className={styles.imageArea}>
                                        <div
                                            className={styles.imagePreview}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            {serviceImg ? (
                                                <img src={serviceImg} alt="service" />
                                            ) : (
                                                <div className={styles.imagePlaceholder}>
                                                    <ImageIcon />
                                                    <span>Nhấn để chọn ảnh</span>
                                                </div>
                                            )}
                                        </div>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={handleImageUpload}
                                        />
                                        <button
                                            className={styles.imageBtn}
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                            style={{ width: '100%' }}
                                        >
                                            <Upload /> {uploading ? 'Đang tải...' : 'Chọn ảnh'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Giá hiện tại (readonly overview) */}
                            {!isCombo && selectedVariant && (
                                <div className={styles.card}>
                                    <div className={styles.cardHeader}>Giá hiện tại</div>
                                    <div className={styles.cardBody}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                                <span style={{ color: '#6B7280' }}>Giá bán</span>
                                                <span style={{ fontWeight: 600, color: '#0098F6' }}>{formatCurrency(selectedVariant.retailPrice)}</span>
                                            </div>
                                            {Number(selectedVariant.salePrice) > 0 && (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                                    <span style={{ color: '#6B7280' }}>Giá KM</span>
                                                    <span style={{ fontWeight: 600, color: '#EF4444' }}>
                                                        {formatCurrency(selectedVariant.salePrice)}
                                                    </span>
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                                <span style={{ color: '#6B7280' }}>Giá nhập</span>
                                                <span style={{ fontWeight: 500, color: '#F59E0B' }}>{formatCurrency(selectedVariant.importPrice)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Toast / Success modal */}
            {toastMsg && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div
                            className={styles.successIcon}
                            style={toastMsg.type === 'error' ? { background: '#EF4444' } : undefined}
                        >
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span className={styles.successText}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
